/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { useToasts } from 'react-toast-notifications'

import eventApi from '_api/event'
import userApi from '_api/user'
import useQuery from '_hooks/useQuery'
import { deleteEvent, getEventAndOrder } from '_redux/modules/event'
import { removeEventInGroup } from '_redux/modules/group'
import { deleteOrder } from '_redux/modules/order'
import {
  BUYER_ORDER_CANCELED,
  BUYER_PAID,
  DESKTOP,
  HOST_ORDER_CANCELED,
  HOST_ORDER_VERIFIED,
} from '_utils/constant'

import { convertTimestampToCurrentGMT, convertToCurrentGMT, handleDataOrder, handleDataOrderByPercentage } from '_utils/function'
import { getDeviceUser } from '_utils/localData'

import NavBar from './NavBar'
import DiscountTicket from './components/DiscountTicket'
import OrderHeader from './components/OrderHeader'
import SearchBar from './components/SearchBar'
import TableOrderList from './components/TableOrderList'
import './style.scss'
import SalesBox from './components/SalesBox'

function ManageOrder() {
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const history = useHistory()
  const params = useQuery()
  const isByAdmin = params.get('isByAdmin') === 'true'

  const eventId = params.get('eventId')
  const { eventAndOrderById, errorGetEventAndOrder } = useSelector((state) => state.event)

  const {
    title,
    closingTimestamp,
    pickupTimestamp,
    closingTime,
    adminCost,
    discount,
    deliveryCost,
    eventCost,
    pickupTime,
    id,
    shareCode,
    orders,
    productIdList,
    status,
    payable,
    paynow,
    groupId,
    totalOrderCount,
    isPercentage = false,
    orderDiscountEnabled,
    orderDiscount,
  } = eventAndOrderById.find((item) => item.id === eventId) || {}
  const {
    listOrder = [],
    totalAllOrder,
    adminCostAllOrder,
    paymentCollected,
    totalPriceAfterDiscount,
    hitpayFeeAllOrder = 0,
  } = productIdList && productIdList.length > 0
      ? isPercentage
        ? handleDataOrderByPercentage(
          orders,
          adminCost,
          discount,
          deliveryCost,
          [...productIdList],
          orderDiscountEnabled,
          orderDiscount
        )
        : handleDataOrder(
          orders,
          adminCost,
          discount,
          deliveryCost,
          [...productIdList],
          orderDiscountEnabled,
          orderDiscount
        )
      : {}

  const [timeRender, setTimeRender] = useState(0)
  const [sorting, setSorting] = useState({ field: 'eOrderNo', order: 'asc', type: 'number' })

  const [_totalOrderCount, setTotalOrderCount] = useState(totalOrderCount)
  const [searchString, setSearchString] = useState('')

  const [isLoading, setIsLoading] = useState(true)

  const initialOrders = async (_eid) => {
    setIsLoading(true)
    await dispatch(getEventAndOrder(_eid, isByAdmin))
    setIsLoading(false)
  }

  useEffect(() => {
    initialOrders(eventId)
  }, [eventId])

  useEffect(() => {
    dispatch(getEventAndOrder(eventId, isByAdmin))
  }, [timeRender])

  useEffect(() => {
    setTotalOrderCount(totalOrderCount)
  }, [totalOrderCount])

  const handelBroadcast = (mess) => {
    if (listOrder && listOrder.length > 0) {
      const listOrderExcludeCancelled = listOrder.filter(
        (item) => item.status !== HOST_ORDER_CANCELED && item.buyerStatus !== BUYER_ORDER_CANCELED
      )
      try {
        listOrderExcludeCancelled.forEach(async (e) => {
          await userApi.createNotifications({
            receiverId: e.uid,
            title: `Broadcast from [Event ${title}]`,
            message: mess,
          })
        })
        addToast('Message sent!', { appearance: 'success', autoDismiss: true })
      } catch (error) {
        addToast(error, { appearance: 'error', autoDismiss: true })
      }
    }
  }

  const isBuyerPaid = (_orders) => {
    if (_orders && _orders.length > 0) {
      return _orders.some(
        (e) => Number(e.buyerStatus) >= BUYER_PAID || Number(e.status) >= HOST_ORDER_VERIFIED
      )
    }
    return false
  }

  const onDeleteClicked = (_id, _orders, _groupId) => {
    if (!isBuyerPaid(_orders)) {
      const r = confirm('Are you sure?')
      if (r === true) {
        eventApi
          .deleteEvent(_id)
          .then(() => {
            dispatch(deleteEvent(_id))
            dispatch(deleteOrder(_id))
            dispatch(removeEventInGroup({ id: _id, groupId: _groupId }))
            if (getDeviceUser() === DESKTOP) {
              history.push('/event/list-hosted-by-group')
            } else {
              history.push('/event/list-my-events')
            }
          })
          .catch((error) => {
            const { msgResp } = error
            addToast(msgResp, { appearance: 'error', autoDismiss: true })
          })
      }
    } else {
      alert("Can't remove an event that has paid orders")
    }
  }

  const onChangeSearch = (event) => {
    setSearchString(event.target.value)
  }

  const listOrders = useMemo(() => {
    const codeRef = searchString.split('-')[1] // to exclude S- in search string, Ex: S-123456 -> 123456
    if (listOrder && listOrder.length > 0) {
      let listOrderSort = listOrder.map((item) => {
        const totalQuantity = (item.allProductPerOrder || []).reduce(
          (sum, p) => sum + (p.pQuantity || 0),
          0
        )
        return { ...item, totalQuantity }
      })
      if (searchString) {
        listOrderSort = listOrderSort.filter(
          (item) =>
            item.uPhone?.toString().includes(searchString) ||
            item.uName?.toLowerCase().includes(searchString.toLowerCase()) ||
            item.ref?.slice(0, 6).includes(codeRef?.toLowerCase())
        )
      }
      if (sorting.field) {
        const reversed = sorting.order === 'asc' ? 1 : -1
        if (sorting.type === 'string') {
          listOrderSort = listOrderSort.sort(
            (a, b) => reversed * (a[sorting.field] || '').localeCompare(b[sorting.field] || '')
          )
        } else {
          listOrderSort = listOrderSort.sort(
            (a, b) => reversed * ((a[sorting.field] || 0) - (b[sorting.field] || 0))
          )
        }
      }
      return listOrderSort
    }
    return []
  }, [sorting, searchString, listOrder])
  const currentEvent = eventAndOrderById.find((event) => event.id === eventId)

  return (
    <React.Fragment>
      <div id='manage-order-page'>
        {isLoading ? <div>Loading...</div> : null}
        {!isLoading && errorGetEventAndOrder ? <div>No Event Found</div> : null}
        {!isLoading && !errorGetEventAndOrder ? (
          <>
            <div className='row-top'>
              <OrderHeader
                title={title}
                closingTimestamp={convertTimestampToCurrentGMT(closingTimestamp)}
                pickupTimestamp={convertTimestampToCurrentGMT(pickupTimestamp)}
                closingTime={convertToCurrentGMT(closingTime)}
                pickupTime={convertToCurrentGMT(pickupTime)}
                id={id}
                numBuy={_totalOrderCount}
                eventAndOrderById={eventAndOrderById.find((item) => item.id === eventId)}
              />
              <NavBar
                eventId={eventId}
                eventAndOrderById={eventAndOrderById.find((item) => item.id === eventId)}
                handelBroadcast={handelBroadcast}
                status={status}
                setTimeRender={setTimeRender}
                payable={payable}
                paynow={paynow}
                isBuyerPaid={isBuyerPaid(orders)}
                products={currentEvent?.products || []}
              />
              <DiscountTicket
                id={id}
                deliveryCost={deliveryCost}
                discount={discount}
                payable={payable}
                status={status}
                eventAndOrderById={eventAndOrderById.find((item) => item.id === eventId)}
                setTimeRender={setTimeRender}
                paynow={paynow}
                isBuyerPaid={isBuyerPaid(orders)}
                isPercentage={isPercentage}
                orderDiscountEnabled={orderDiscountEnabled}
                orderDiscount={orderDiscount}
              />
              <SearchBar
                searchString={searchString}
                onChange={onChangeSearch}
                styles={{ marginTop: '1rem' }}
              />
              {orders && orders.length ? (
                <TableOrderList
                  listOrder={listOrders}
                  setTimeRender={setTimeRender}
                  id={id}
                  products={currentEvent?.products || []}
                  sorting={sorting}
                  setSorting={setSorting}
                />
              ) : null}
            </div>
            <SalesBox
              totalPriceAfterDiscount={totalPriceAfterDiscount}
              totalAllOrder={totalAllOrder}
              adminCostAllOrder={adminCostAllOrder}
              paymentCollected={paymentCollected}
              eventCost={eventCost}
              hitpayFeeAllOrder={hitpayFeeAllOrder}
              id={id}
            />
            <p
              className='link-share'
              style={{ cursor: 'pointer', textAlign: 'left', color: 'red', fontSize: '11px' }}
              onClick={() => {
                if (isByAdmin) {
                  addToast('You are not allowed to delete order', {
                    appearance: 'error',
                    autoDismiss: true,
                  })
                  return
                }

                onDeleteClicked(id, orders, groupId)
              }}
            >
              DELETE
            </p>
          </>
        ) : null}
      </div>
    </React.Fragment>
  )
}

export default ManageOrder
